import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import React, { useEffect, useMemo, useState } from "react";

import { Session } from "../../../../types";

interface SessionDatePickerProps {
  selectedDates: Date[];
  onDateChange: (selectedDates: Date[]) => void;
  sessions: Session[];
  onCancel: () => void;
  onApply: () => void;
  /**
   * Optional cap on the number of dates that can be selected at once.
   * Omitted (default) means unlimited.
   */
  maxSelectableDates?: number;
}

export const SessionDatePicker: React.FC<SessionDatePickerProps> = ({
  selectedDates,
  onDateChange,
  sessions,
  onCancel,
  onApply,
  maxSelectableDates,
}) => {
  const [currentMonth, setCurrentMonth] = useState(
    selectedDates.length > 0 ? selectedDates[0] : new Date(),
  );
  const [tempSelectedDates, setTempSelectedDates] =
    useState<Date[]>(selectedDates);

  // Update temp dates when props change
  useEffect(() => {
    setTempSelectedDates(selectedDates);
  }, [selectedDates]);

  // Extract unique session dates
  const sessionDates = useMemo(() => {
    const dates = new Set<string>();
    sessions.forEach((session) => {
      if (session.sessionDate) {
        try {
          const date = parseISO(session.sessionDate);
          const dateKey = format(startOfDay(date), "yyyy-MM-dd");
          dates.add(dateKey);
        } catch {
          // Invalid date, skip
        }
      }
    });
    return dates;
  }, [sessions]);

  // Check if a date has a session
  const hasSession = (date: Date) => {
    const dateStr = format(startOfDay(date), "yyyy-MM-dd");
    const hasSessionResult = sessionDates.has(dateStr);
    return hasSessionResult;
  };

  // Check if date is selected
  const isSelected = (date: Date) => {
    return tempSelectedDates.some((selectedDate) =>
      isSameDay(date, selectedDate),
    );
  };

  const isAtMaxSelectableDates =
    maxSelectableDates !== undefined &&
    tempSelectedDates.length >= maxSelectableDates;

  const today = startOfDay(new Date());
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ["Mo", "Tu", "We", "Th", "Fri", "Sa", "Su"];

  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  // Shared toggle used by both the calendar grid and the Recent Sessions
  // list. Blocks adding a new date once at the cap; deselection is always
  // allowed.
  const toggleDateSelection = (day: Date) => {
    const normalizedDay = startOfDay(day);
    const isCurrentlySelected = tempSelectedDates.some((selectedDate) =>
      isSameDay(normalizedDay, selectedDate),
    );

    if (!isCurrentlySelected && isAtMaxSelectableDates) return;

    let newSelectedDates: Date[];
    if (isCurrentlySelected) {
      // Remove date if already selected
      newSelectedDates = tempSelectedDates.filter(
        (selectedDate) => !isSameDay(normalizedDay, selectedDate),
      );
    } else {
      // Add date if not selected
      newSelectedDates = [...tempSelectedDates, normalizedDay];
    }

    setTempSelectedDates(newSelectedDates);
    onDateChange(newSelectedDates);
  };

  const handleDateClick = (day: Date) => {
    if (!hasSession(day) || isAfter(startOfDay(day), today)) return;
    toggleDateSelection(day);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    const nextMonth = addMonths(currentMonth, 1);
    if (!isAfter(startOfMonth(nextMonth), today)) {
      setCurrentMonth(nextMonth);
    }
  };

  const canGoToNextMonth = () => {
    const nextMonth = addMonths(currentMonth, 1);
    return !isAfter(startOfMonth(nextMonth), today);
  };

  // Get recent unique dates (grouped by date, sorted by date, most recent first)
  const recentDates = useMemo(() => {
    // Group sessions by date
    const dateMap = new Map<
      string,
      { date: Date; count: number; sessionType?: string }
    >();

    sessions
      .filter((s) => s.sessionDate)
      .forEach((s) => {
        try {
          const parsedDate = parseISO(s.sessionDate);
          const dateKey = format(startOfDay(parsedDate), "yyyy-MM-dd");

          if (dateMap.has(dateKey)) {
            const existing = dateMap.get(dateKey)!;
            existing.count += 1;
            // Clear sessionType if multiple sessions
            if (existing.count > 1) {
              existing.sessionType = undefined;
            }
          } else {
            dateMap.set(dateKey, {
              date: parsedDate,
              count: 1,
              sessionType: s.sessionType.name,
            });
          }
        } catch {
          // Invalid date, skip
        }
      });

    // Convert to array, sort by date (most recent first), and take top 6
    return Array.from(dateMap.values())
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 6);
  }, [sessions]);

  return (
    <div className="bg-[#1d1d1f] content-stretch flex items-start relative rounded-[6px] size-full">
      {/* Left Panel - Recent Sessions */}
      <div className="bg-[#1d1d1f] relative rounded-bl-[6px] rounded-tl-[12px] self-stretch shrink-0 w-[197px] border-r border-[#9f9fa1]">
        <div className="content-stretch flex flex-col h-full items-start overflow-clip relative rounded-[inherit] w-[195px]">
          <div className="box-border content-stretch flex flex-col items-start px-0 py-[4px] relative rounded-tl-[12px] shrink-0 w-full">
            {/* Recent sessions header */}
            <div className="relative shrink-0 w-full">
              <div className="flex flex-row items-center size-full">
                <div className="box-border content-stretch flex gap-[12px] items-center pb-0 pt-[8px] px-[16px] relative w-full">
                  <div className="basis-0 box-border content-stretch flex flex-col gap-[2px] grow items-start min-h-px min-w-px px-0 py-[8px] relative shrink-0">
                    <p className="font-medium leading-[20px] not-italic relative shrink-0 text-white text-[12px] text-nowrap whitespace-pre">
                      Recent sessions
                    </p>
                    {maxSelectableDates !== undefined && (
                      <p
                        data-testid="session-date-picker-max-dates-subtitle"
                        className="font-normal leading-[16px] not-italic relative shrink-0 text-[#7e7e81] text-[11px] text-nowrap whitespace-pre"
                      >
                        Select up to {maxSelectableDates} sessions
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Session list */}
            {recentDates.map((dateInfo, index) => {
              const sessionDay = startOfDay(dateInfo.date);
              const isSessionSelected = tempSelectedDates.some((selectedDate) =>
                isSameDay(sessionDay, selectedDate),
              );

              const isRowDisabled =
                !isSessionSelected && isAtMaxSelectableDates;

              const displayText =
                dateInfo.count > 1
                  ? `${format(dateInfo.date, "M/d/yyyy")} (${dateInfo.count})`
                  : format(dateInfo.date, "M/d/yyyy");

              return (
                <div
                  key={`${format(sessionDay, "yyyy-MM-dd")}-${index}`}
                  data-testid="session-date-picker-recent-session"
                  aria-disabled={isRowDisabled}
                  className={`relative shrink-0 w-full transition-colors ${
                    isRowDisabled
                      ? "opacity-40 cursor-not-allowed"
                      : "cursor-pointer hover:bg-[#2a2a2a]"
                  }`}
                  onClick={() =>
                    !isRowDisabled && toggleDateSelection(sessionDay)
                  }
                >
                  <div className="flex flex-row items-center size-full">
                    <div className="box-border content-stretch flex gap-[12px] items-center justify-between px-[16px] py-[8px] relative w-full">
                      <p
                        className={`font-medium leading-[20px] not-italic relative shrink-0 text-[14px] text-nowrap whitespace-pre ${
                          isSessionSelected ? "text-[#fb6a07]" : "text-white"
                        }`}
                      >
                        {displayText}
                      </p>
                      {isSessionSelected && (
                        <CheckCircleIcon
                          data-testid="session-date-picker-recent-session-check"
                          className="w-5 h-5 text-[#fb6a07] shrink-0"
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Spacer */}
            <div className="basis-0 grow h-[36px] min-h-px min-w-px shrink-0" />
          </div>
        </div>
      </div>

      {/* Right Panel - Date Picker */}
      <div className="bg-[#1d1d1f] box-border content-stretch flex h-[397px] items-start relative rounded-br-[12px] rounded-tr-[12px] shadow-[0px_30px_84px_0px_rgba(19,10,46,0.08),0px_8px_32px_0px_rgba(19,10,46,0.07),0px_3px_14px_0px_rgba(19,10,46,0.03),0px_1px_3px_0px_rgba(19,10,46,0.13)] shrink-0 flex-1">
        <div className="content-stretch flex flex-col h-full items-start relative shrink-0 w-full">
          {/* Calendar */}
          <div className="basis-0 grow min-h-px min-w-px relative shrink-0 w-full">
            <div className="size-full">
              <div className="box-border content-stretch flex flex-col items-start pb-[12px] pt-[16px] px-[24px] relative size-full">
                {/* Calendar Header */}
                <div className="content-stretch flex items-center justify-center relative shrink-0 w-full">
                  <button
                    className="box-border content-stretch flex gap-2 items-center justify-center p-1 relative rounded-lg shrink-0 cursor-pointer focus:outline-none"
                    onClick={handlePrevMonth}
                    type="button"
                  >
                    <ChevronLeftIcon className="w-4 h-4 text-white" />
                  </button>
                  <div className="basis-0 box-border content-stretch flex gap-2 grow items-center justify-center min-h-px min-w-px px-0 py-[8px] relative rounded-lg shrink-0">
                    <p className="font-medium leading-6 not-italic relative shrink-0 text-sm text-nowrap text-white whitespace-pre">
                      {format(currentMonth, "MMMM yyyy")}
                    </p>
                  </div>
                  <div className="flex items-center justify-center relative shrink-0">
                    <button
                      className={`box-border content-stretch flex gap-2 items-center justify-center p-1 relative rounded-lg focus:outline-none ${
                        !canGoToNextMonth()
                          ? "opacity-40 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                      onClick={handleNextMonth}
                      type="button"
                      disabled={!canGoToNextMonth()}
                    >
                      <ChevronRightIcon className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>

                {/* Week days header */}
                <div className="basis-0 content-stretch flex grow items-start justify-center min-h-px min-w-px relative shrink-0 w-full mt-2">
                  {weekDays.map((day) => (
                    <div
                      key={day}
                      className="basis-0 grow h-full min-h-px min-w-px overflow-clip relative rounded-lg shrink-0"
                    >
                      <p className="absolute font-medium leading-6 left-1/2 not-italic text-sm text-center text-white top-1/2 -translate-x-1/2 -translate-y-1/2 w-6">
                        {day}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                {weeks.map((week, weekIndex) => (
                  <div
                    key={weekIndex}
                    className="basis-0 content-stretch flex grow items-center min-h-px min-w-px relative shrink-0 w-full gap-0"
                    style={{ minHeight: "32px" }}
                  >
                    {week.map((day) => {
                      const isCurrentMonth = isSameMonth(day, currentMonth);
                      const dayHasSession = hasSession(day);
                      const dayIsSelected = isSelected(day);
                      const isFuture = isAfter(startOfDay(day), today);
                      const isDisabled =
                        !dayHasSession ||
                        isFuture ||
                        !isCurrentMonth ||
                        (!dayIsSelected && isAtMaxSelectableDates);

                      let cellClassName =
                        "basis-0 grow h-full min-h-[32px] min-w-px overflow-hidden relative shrink-0 flex items-center justify-center";

                      if (dayIsSelected) {
                        cellClassName += " bg-[#fb6a07] rounded-[6px]";
                      } else if (dayHasSession && isCurrentMonth && !isFuture) {
                        cellClassName +=
                          " border border-[#fb6a07] rounded-[6px]";
                      }

                      if (isDisabled) {
                        cellClassName += " cursor-not-allowed opacity-40";
                      } else {
                        cellClassName += " cursor-pointer";
                      }

                      let textColor = "text-white";
                      if (!isCurrentMonth) {
                        textColor = "text-[#7e7e81]";
                      }

                      return (
                        <div
                          key={day.toISOString()}
                          data-testid="session-date-picker-calendar-day"
                          data-date={format(day, "yyyy-MM-dd")}
                          aria-disabled={isDisabled}
                          className={cellClassName}
                          onClick={() => !isDisabled && handleDateClick(day)}
                        >
                          <p
                            className={`font-medium leading-6 not-italic text-sm text-center ${textColor}`}
                          >
                            {format(day, "d")}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Cancel and Apply Buttons */}
          <div className="relative shrink-0 w-full border-t border-[#48474d]">
            <div className="flex flex-row items-center size-full">
              <div
                className={`box-border content-stretch flex gap-[8px] items-center px-[24px] py-[12px] relative w-full ${
                  maxSelectableDates !== undefined
                    ? "justify-between"
                    : "justify-end"
                }`}
              >
                {maxSelectableDates !== undefined && (
                  <p
                    data-testid="session-date-picker-selection-count"
                    className="font-normal leading-[16px] not-italic text-[#7e7e81] text-[12px]"
                  >
                    {tempSelectedDates.length} of {maxSelectableDates} selected
                  </p>
                )}
                <div className="flex gap-[8px] items-center">
                  <button
                    onClick={onCancel}
                    data-testid="session-date-picker-cancel-button"
                    className="h-full relative rounded-[6px] shrink-0 w-[91px] border border-[#fb6a07] border-solid"
                  >
                    <div className="box-border content-stretch flex gap-[4px] h-full items-center justify-center overflow-clip px-[13px] py-[9px] relative rounded-[inherit] w-[91px]">
                      <p className="font-medium leading-[24px] not-italic relative shrink-0 text-[16px] text-nowrap text-white whitespace-pre">
                        Cancel
                      </p>
                    </div>
                  </button>
                  <button
                    onClick={onApply}
                    data-testid="session-date-picker-apply-button"
                    className="bg-[#fb6a07] h-full relative rounded-[6px] shrink-0 w-[91px]"
                  >
                    <div className="box-border content-stretch flex gap-[4px] h-full items-center justify-center overflow-clip px-[13px] py-[9px] relative rounded-[inherit] w-[91px]">
                      <p className="font-medium leading-[24px] not-italic relative shrink-0 text-[16px] text-nowrap text-white whitespace-pre">
                        Apply
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
